import { string, z } from 'zod';
import { Vendor } from './vendor';

export const EventCategorySchema = z.enum([
  'workshop',
  'seminar',
  'social',
  'meeting',
  'training'
]);

export type EventCategory = z.infer<typeof EventCategorySchema>;

export const TierClassificationSchema = z.enum([
  'universal',
  'targeted',
  'intensive'
]);

export type TierClassification = z.infer<typeof TierClassificationSchema>;

export const LocationTypeSchema = z.enum([
  'physical',
  'virtual',
  'hybrid'
]);

export type LocationType = z.infer<typeof LocationTypeSchema>;

export const EventStatusSchema = z.enum([
  'draft',
  'pending_approval',
  'approved',
  'in_progress',
  'completed',
  'cancelled'
]);

export type EventStatus = z.infer<typeof EventStatusSchema>;

export const FrequencyTypeSchema = z.enum([
  'one_time',
  'multiple_times'
]);

export type FrequencyType = z.infer<typeof FrequencyTypeSchema>;

export const BudgetItemSchema = z.object({
  id: z.string(),
  category: z.string().min(1, 'Category is required'),
  description: z.string().min(1, 'Description is required'),
  estimatedCost: z.number().min(0, 'Estimated cost cannot be negative'),
  actualCost: z.number().nullable().default(null),
  receipts: z.array(z.object({
    id: z.string(),
    url: z.string(),
    filename: z.string(),
    uploadedAt: z.string()
  })).default([])
});

export type BudgetItem = z.infer<typeof BudgetItemSchema>;

export const TaskStatusSchema = z.enum([
  'not_started',
  'in_progress',
  'completed',
  'blocked',
]);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  assignedTo: z.string(),
  dueDate: z.string(),
  status: TaskStatusSchema,
  priority: z.enum(['low', 'medium', 'high']),
  comments: z.array(z.object({
    id: z.string(),
    userId: z.string(),
    content: z.string(),
    timestamp: z.string()
  }))
})

export const ResourceTypeSchema = z.enum([
  'equipment',
  'material',
  'venue',
  'personnel',
]);

export type ResourceType = z.infer<typeof ResourceTypeSchema>;


export const ResourceSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Resource name is required'),
  type: ResourceTypeSchema,
  quantity: z.number().default(0),
  available: z.number().default(0),
  url: z.string(),
  vendor: z.object({
    name: z.string().optional(),
    contactName: z.string().optional(),
    email: z.string().optional(),
  }).nullable().optional(),
  notes: z.string(),
});



export type Resource = z.infer<typeof ResourceSchema>;

export const FeedbackSchema = z.object({
  id: z.string(),
  type: z.enum(['pre', 'post']),
  responses: z.array(z.object({
    questionId: z.string(),
    response: z.union([z.string(), z.number(), z.boolean()]),
    timestamp: z.string()
  }))
});

export type Feedback = z.infer<typeof FeedbackSchema>;


export const ComplianceItemSchema = z.object({
  id: z.string(),
  requirement: z.string(),
  status: z.enum(['pending', 'completed', 'not_applicable']),
  dueDate: z.string(),
  documents: z.array(z.object({
    id: z.string(),
    url: z.string(),
    filename: z.string(),
    uploadedAt: z.string()
  }))
});

export type ComplianceItem = z.infer<typeof ComplianceItemSchema>;

export const EventSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  title: z.string().min(1, 'Title is required'),
  description: z.string().default(''),
  startDate: z.string(),
  endDate: z.string(),
  tierClassification: TierClassificationSchema.default('universal'),
  locationType: LocationTypeSchema,
  location: z.string().default(''),
  virtualLink: z.string().optional(),
  eventCategory: EventCategorySchema,
  status: EventStatusSchema,
  objectives: z.array(z.string()).default([]),
  targetGrades: z.array(z.number()).default([]),
  eventResources: z.array(ResourceSchema).default([]),
  staffAssignments: z.array(z.object({
    id: z.string(),
    name: z.string(),
    userId: z.string(),
    status: z.enum(['pending', 'approved', 'rejected']),
    comments: z.string().optional(),
    timestamp: z.string()
  })).default([]),
  frequencyType: FrequencyTypeSchema.default('one_time'),
  occurrences: z.array(z.string()).default([]),
  budget: z.array(BudgetItemSchema).default([]),
  tasks: z.array(TaskSchema).default([]),
  resources: z.array(ResourceSchema).default([]),
  feedback: z.array(FeedbackSchema).default([]),
  compliance: z.array(ComplianceItemSchema).default([]),
  metrics: z.object({
    attendance: z.number().default(0),
    satisfaction: z.number().default(0),
    budget: z.object({
      estimated: z.number().nonnegative().default(0),
      actual: z.number().nonnegative().nullable().default(null),
      variance: z.number().default(0)
    }).default({})
  }).default({})
});

export type Event = z.infer<typeof EventSchema>;
export type Task = z.infer<typeof TaskSchema>;